/**
 * V7-P4-NAV-COHERENCE-AND-ROLE-SHELL-V1 -- THE ONE ROLE-AWARE NAV CONFIG.
 *
 * Single source of truth for every dashboard sidebar section and item.
 * Before this package the item lists lived as six hardcoded JSX components
 * inside DashboardLayout.tsx (OwnerNav / AgmNav / CocNav / DoctorNav /
 * ReceptionistNav / CallCenterNav); Benchmark Book A verdict A2 (global
 * navigation, ADAPT) and issue #285 (FND-24) require ONE navigation
 * architecture instead. This module is pure data + pure resolvers -- no
 * React, no CSS -- so the root vitest suite can pin it and the renderer
 * (DashboardNavList in DashboardLayout.tsx) stays a thin mapping.
 *
 * WHAT THIS MODULE DOES NOT DECIDE:
 *  - WHICH sections a role sees: that stays the backend's
 *    lib/dashboard-role-view.ts (source of truth behind /api/dashboard/me),
 *    mirrored by ./role-view.ts and drift-pinned by the equivalence test.
 *    This config maps section -> items; me.navSections maps role -> sections.
 *  - AUTHORITY: nav visibility is UX cleanliness only; backend route auth
 *    remains the security boundary (RequireNavSection + api/routes/*).
 *
 * FAIL-CLOSED LAW (issue #285 acceptance): an unknown role resolves to
 * navSections [] (role-view.ts / dashboard-role-view.ts default arms), and
 * an unknown SECTION token in me.navSections resolves to nothing here
 * (resolveNavSections filters against the known order; resolveNavItems
 * returns [] for an unknown section). The patient audience renders nothing
 * gated. Null me (loading / errored) renders nothing.
 *
 * ITEM GATES (all fail-closed, absence = hidden):
 *  - requiresSignal: a server-computed /me boolean (never a client guess) --
 *    the #294 door pattern: the item appears if and ONLY IF its route is
 *    lawfully reachable, so it is never a dead link (LES-016: links are
 *    role-shaped or absent).
 *  - onlyRole: narrows an item inside a SHARED section (gm_agm-only Test
 *    journey inside the shared 'agm' section; regional_head never had it).
 *  - signalTo: retargets an always-present slot when a signal is on (the
 *    V5-P10 LEADS-CANONICAL-FOLD owner Leads slot).
 *
 * COMING-SURFACE SLOT (designed, deliberately NOT stubbed): the COC /
 * call-center workspace (docs/requirements/partha-dental/
 * CALL-CENTER-WORKSPACE-2026-07-19.md s.1, the Deepak left-nav model) will
 * join as ITEMS on the existing 'call_center' section (and, where lawful,
 * new sections in DASHBOARD_NAV_SECTION_ORDER) gated on server-computed
 * signals exactly like every item below. Repo law forbids a disabled or
 * dead nav entry (LES-016; the V5-P9 DELTA-FOR-FOUNDER precedent shipped
 * ONLY the reachable surface), so the slot ships as this documented
 * insertion point, never a dead door. No shell rework is needed to land it:
 * adding a workspace = appending NavItemConfig rows here + routes in
 * App.tsx.
 */

import type { DashboardMe, DashboardNavSection } from './types';

/**
 * Server-computed /me boolean signals the nav consumes. Every key is an
 * optional boolean on DashboardMe whose ABSENCE means hidden (fail-closed);
 * the backend computes each one only when the route is lawfully reachable
 * for the caller (api/router.ts GET /api/dashboard/me).
 */
export type NavSignal =
  | 'leadsWorkspaceEnabled'
  | 'patientMasterEnabled'
  | 'patientMasterMergeEnabled'
  | 'intakeRoutedToRole'
  | 'doctorLensEnabled'
  | 'callCenterWorkspaceEnabled'
  | 'followupQueueEnabled'
  | 'followupOwnerQueueEnabled'
  | 'missedAppointmentQueueEnabled'
  | 'testJourneyEnabled';

export interface NavItemConfig {
  readonly label: string;
  readonly to: string;
  /** react-router NavLink `end` matching (active only on the exact path). */
  readonly end?: boolean;
  /** Render only while this server-computed /me signal is strictly true. */
  readonly requiresSignal?: NavSignal;
  /** Render only for this exact role inside a shared section. */
  readonly onlyRole?: DashboardMe['role'];
  /** Alternate target while a signal is on (the owner Leads fold, V5-P10). */
  readonly signalTo?: { readonly signal: NavSignal; readonly to: string };
  /**
   * Render as a plain Link with no active-state class (the receptionist
   * /walkin Today door -- the pre-config markup rendered exactly this).
   */
  readonly plainLink?: boolean;
  /** Same scoped reader as the Doctor Pending page. */
  readonly count?: 'doctorPending';
}

export interface NavSectionConfig {
  /** The sidebar section header label (rendered by DashboardNavList). */
  readonly header: string;
  readonly items: readonly NavItemConfig[];
}

/**
 * Stable section render order (unchanged from the pre-config
 * DashboardNavList): Owner -> AGM -> COC -> Doctor -> Front desk ->
 * Call center. Spacing on second-and-later visible headers is the
 * renderer's job so the first visible header never gets a top margin.
 */
export const DASHBOARD_NAV_SECTION_ORDER: readonly DashboardNavSection[] = [
  'owner',
  'agm',
  'coc',
  'doctor',
  'receptionist',
  'call_center',
];

/**
 * The one section -> items map. Item-level law citations are preserved from
 * the JSX components this config replaces; the render output is
 * byte-identical (pinned per role in DashboardLayout.test.tsx +
 * nav-config.test.ts).
 */
export const DASHBOARD_NAV_CONFIG: Readonly<
  Record<DashboardNavSection, NavSectionConfig>
> = {
  /*
   * OWNER-JOURNEY-STITCH-V1 (V5-P8): the J1 designed five (03-ROLE-JOURNEYS
   * J1: Overview | Leads | Growth | Clinics | Settings) per the max-5
   * role-home law (06-CRM-ROLE-VISIBILITY-DOCTRINE rule 3); removed links
   * stay reachable as Overview drills (rule 1 read-down intact).
   * LEADS-CANONICAL-FOLD-V1 (V5-P10): the ONE Leads slot retargets to the
   * canonical list when leadsWorkspaceEnabled is on (count never changes).
   * ROLE-DISCOVERABILITY-LAWFUL-COVERAGE-V1 (founder ruling 2026-07-17,
   * binding): Owner + Patients = PRIMARY NAVIGATION -- the Patients door is
   * the SIXTH link, gated on the same server-computed patientMasterEnabled
   * signal the COC / AGM / receptionist doors use; flag-off baseline stays
   * the J1 five byte-identically. Patient MERGES stay an Overview drill.
   */
  /*
   * OWNER-OPERATING-SYSTEM-V1 (SURVIVORSHIP + HOME RULINGS, 2026-08-02):
   * Overview -> Home (/dashboard/home); Growth + Clinics FOLD into Reports
   * (the HOME RULING: "detailed analysis remains under Reports"; tenancy
   * audit rows 11/18/19) -- hiding those doors deletes nothing, the routes
   * survive as redirects (NAV law: a nav change is never a permission
   * change). The owner "Settings" label becomes the honest "Routing
   * settings" (audit row 28: one label truth with the manager row until a
   * real settings hub exists). Six rows total, within the ruled-addition
   * precedent (J1 five + Patients + Money = seven before this fold).
   */
  owner: {
    header: 'Owner',
    items: [
      { label: 'Home', to: '/dashboard/home', end: true },
      { label: 'Reports', to: '/dashboard/reports' },
      {
        label: 'Leads',
        to: '/dashboard/owner/leads',
        signalTo: { signal: 'leadsWorkspaceEnabled', to: '/dashboard/leads' },
      },
      {
        label: 'Patients',
        to: '/dashboard/patients',
        end: true,
        requiresSignal: 'patientMasterEnabled',
      },
      /*
       * FRONT-OFFICE-OS-V2-BUILD-V1 (founder ruling FO-1 2026-08-01,
       * closing walk finding F5 -- NAV MIRRORS ADMISSION): the owner IS in
       * MONEY_DESK_ROLES (money-desk-view.ts) and lawfully opens
       * /dashboard/money (walk W2 observed the role gate live), but the
       * door lived only in the front-desk section -- admission and
       * advertisement disagreed for a SEEDED role. The founder ruled the
       * entry in (the ruled-addition precedent of the sixth Patients row).
       * A nav entry is never a permission: the server-side accounts fences
       * are byte-unchanged.
       */
      { label: 'Money', to: '/dashboard/money' },
      { label: 'Routing settings', to: '/dashboard/work-routing/settings' },
    ],
  },
  /*
   * The shared manager section (gm_agm + regional_head both map here; scope
   * does the differentiation -- 03-ROLE-JOURNEYS J2/J3).
   * S1-P6 routing-settings link: the manager branch of the routing role set.
   * LEADERSHIP-QUEUE-VISIBILITY-V1 (V2-P1b): Lead operations is the ONE
   * leadership door into the operator cockpit (SEE-NOT-DO).
   * Test journey: gm_agm only (ADORACARE-VISIT-CASE-SPINE-V1;
   * regional_head is outside the journey audience -- no dead links) AND
   * signal-gated (TEST-SPINE-GATING-V1: the row renders only when the
   * server-computed testJourneyEnabled door signal is on).
   */
  agm: {
    header: 'AGM',
    items: [
      { label: 'Zone overview', to: '/dashboard/agm', end: true },
      { label: 'Zone performance', to: '/dashboard/agm/zone' },
      { label: 'Compare clinics', to: '/dashboard/agm/compare' },
      { label: 'Routing settings', to: '/dashboard/work-routing/settings' },
      { label: 'Lead operations', to: '/dashboard/lead-cockpit' },
      {
        label: 'Patients',
        to: '/dashboard/patients',
        end: true,
        requiresSignal: 'patientMasterEnabled',
      },
      {
        label: 'Patient merges',
        to: '/dashboard/patients/merge-queue',
        requiresSignal: 'patientMasterMergeEnabled',
      },
      {
        label: 'Test journey',
        to: '/dashboard/test-journey',
        onlyRole: 'gm_agm',
        // TEST-SPINE-GATING-V1: the row additionally requires the
        // server-computed door signal, so an ungated gm_agm (a production
        // org under the test-org gate) never sees a dead door. Under gating
        // OFF the backend emits the signal for the legacy gm_agm audience,
        // so the row renders exactly as before (the rollback contract).
        requiresSignal: 'testJourneyEnabled',
      },
      /*
       * CALL-CENTER-CRM-WORKSPACE-V1: the manager Team-operations lens of the
       * call-center workspace (hierarchy read-down, visibility doctrine rule
       * 1). Same route as the agent My Work door -- the server serves the
       * manager lens by role. Gated on the same per-org
       * callCenterWorkspaceEnabled signal (seeded OFF); the OWNER's read-down
       * stays a drill (rule 3 -- no seventh owner nav row; recorded in the
       * run record).
       */
      {
        label: 'Team operations',
        to: '/dashboard/callcenter/workspace',
        requiresSignal: 'callCenterWorkspaceEnabled',
      },
      /*
       * CLINIC-CATALOG-PRICING-TRUTH-S1-V1: master-data provenance door for
       * the leadership tier (gm_agm + regional_head are in the FROZEN
       * backend catalog audience -- role-static, no signal). The OWNER's
       * catalog read stays URL/drill-reachable, NOT a seventh owner nav row
       * (rule 3 max-5 + founder-ruled sixth; the Team-operations precedent).
       */
      { label: 'Treatment catalog', to: '/dashboard/treatment-catalog' },
      /*
       * MANAGER-ROLLUP-REACHABILITY-V1 (activity 11 closure): the SAME
       * call-centre Daily report door the call_center section already
       * carries -- AGENT-DAY-PRODUCTIVITY-ROLLUP-V1 composed the leadership
       * tier's per-agent rollup onto that SAME route server-side, but no nav
       * row and no route guard admitted gm_agm / regional_head / owner to
       * find it. Role-static, no signal: like Treatment catalog above, the
       * FROZEN backend audience (CALLCENTER_DAILY_REPORT_ROUTE_AUDIENCE_ROLES,
       * callcenter-daily-report-access.ts) admits this shared section's two
       * roles unconditionally -- this route carries no per-org config gate
       * (CALLCENTRE-EOD-V1 shipped it flag-free), so a signal here would be
       * invented, not matched. The route guard itself moved from
       * RequireNavSection to RequireCallCenterDailyReport (frontend/src/
       * App.tsx) -- the manager-tracking precedent applied to this surface --
       * so this row is never a dead door (LES-016).
       */
      { label: 'Daily report', to: '/dashboard/callcenter/daily-report' },
    ],
  },
  /*
   * The COC nav -- RECONCILED to <=5 doors by COC-EXPERIENCE-V1 (founder UX
   * ruling 1, 2026-08-20; #593 corrective item 3).
   *
   * THE COC HOME IS THE COMMAND CENTER, AND IT IS UNBUILT. The approved COC
   * home is the five-tab command center (docs/design/growth-os/mockups/
   * 03-coc-command-center.html: My Work / Acceptance / Payments / Escalations
   * / Clinic Health). This package built ONLY ONE of those tabs -- the
   * Follow-ups work area (the "Acceptance" tab) over the #589 engine -- and
   * SEATS IT AS A NAV DOOR, never as the home. The COC lands on the interim
   * "My clinics" page (homePath /dashboard/coc, unchanged) until the command
   * center is built.
   *
   * Deepak's CONFIRMED Sept-15 COC job is post-consult FOLLOW-UP (DELTA
   * 2026-08-19 E3.1) over the COCs' assigned clinics (E3.4); a generic,
   * owner-style "Reports" HUB stayed DEFERRED / not-built (E3.7) as of this
   * ruling -- neither door below is that hub; each is ONE scoped report.
   * COC-REPORTING-GRAINS-V1 (activity 21, 2026-08-26) and COC-EOD-REPORT-V1
   * (activity 23, 2026-08-26) landed as two independent packages against the
   * SAME <=5-door ceiling this ruling set; concurrently merged, together
   * they bring this section to 6 doors -- past that ceiling. Neither
   * package alone crossed it; reconciling the two against the <=5 ruling
   * (fold, promote one to the home, or a founder-ruled exception) is a
   * follow-up decision, not either individual package's call to make
   * unilaterally. The six nav doors:
   *   - My clinics      -> the assigned-clinic ops page (the INTERIM home,
   *                        E3.4).
   *   - Follow-ups      -> the Follow-ups WORK AREA over the #589 engine (a
   *                        door, not the home).
   *   - No-shows        -> the COC no-show recovery desk (Partha SLA item 5).
   *   - Billing         -> COC billing-submission compliance.
   *   - Follow-up report -> clinic-wise + treatment-wise counts over COC's
   *                        own open follow-ups (activity 21).
   *   - Daily report    -> the date-navigable EOD report (activity 23):
   *                        what happened on a given day, over facts already
   *                        recorded -- never a live board, never a second
   *                        Follow-ups queue.
   *
   * REMOVED FROM COC NAV (routes + backend auth BYTE-UNCHANGED -- a nav
   * change is never a permission change; every removed page keeps its route
   * guard and stays URL-reachable for any role the server still admits):
   *   Workboard (/dashboard/workboard) + Lead to appointment
   *   (/dashboard/lead-cockpit) + Org intake + Lead inbox + Leads -- the
   *   front-half LEAD/intake doors: "COC-as-default-intake-owner" is the
   *   OLDER behavior the founder ruled must NOT auto-remain (ruling 1); no
   *   CONFIRMED law after 2026-08-20 requires them for COC (DELTA 2026-08-19
   *   requires none). Clinical workbench + Patient merges + Billing entry +
   *   Money + the generic Patients browse -- the founder's explicit "do NOT
   *   expose" list (ruling 1). The patient-record drill still opens
   *   /dashboard/patients/:id from a queue row when Patient Master is on (a
   *   drill, never a nav door); the clinical-workbench ROUTE keeps its
   *   deployed-proof walk coverage off-nav (roles.ts coc-clinical, #593
   *   corrective item 2).
   */
  coc: {
    header: 'COC',
    items: [
      { label: 'My clinics', to: '/dashboard/coc', end: true },
      { label: 'Doctor coverage', to: '/dashboard/coc/coverage', end: true },
      { label: 'Follow-ups', to: '/dashboard/coc/patient-followups', end: true },
      { label: 'No-shows', to: '/dashboard/coc/no-shows' },
      { label: 'Billing', to: '/dashboard/coc/billing' },
      /*
       * COC-REPORTING-GRAINS-V1 (activity 21, 2026-08-26): ONE scoped
       * report door -- clinic-wise + treatment-wise counts over COC's own
       * open follow-ups -- NOT a generic owner-style "Reports" hub (that
       * stays DEFERRED / not-built per this section's own ruling above).
       * Named "Follow-up report" rather than "Reports" specifically to
       * keep that distinction legible in the nav itself.
       */
      { label: 'Follow-up report', to: '/dashboard/coc/followup-report' },
      { label: 'Daily report', to: '/dashboard/coc/daily-report' },
    ],
  },
  /*
   * FND-29-ROLE-DISCOVERABILITY-CORRECTIVE-V1 (#297): "Today" (the queue)
   * is the single honest primary entry into clinical work; the consult
   * workspace opens per queue row (NO-MANUAL-ID LAW). The former standalone
   * "Clinical workspace" door was a dead/misleading link and stays removed.
   * Frozen V14g: Home / Pending / Reports. The existing Patients deep
   * route retains its doctorLensEnabled admission and read policy.
   */
  doctor: {
    header: 'Doctor',
    items: [
      /*
       * DOCTOR-PRECALL-POLISH-V1 (DR-19): the landing is HOME -- it composes
       * the whole day (tiles + owed evidence + the queue), not one list, so
       * "Today" under-named it. The named route is /dashboard/doctor/home;
       * /dashboard/doctor redirects there (App.tsx).
       */
      { label: 'Home', to: '/dashboard/doctor/home', end: true },
      { label: 'Pending', to: '/dashboard/doctor/pending', count: 'doctorPending' },
      { label: 'Reports', to: '/dashboard/clinic-reports' },
      // Wave 1 D09: catalog selection stays inside consult; no standalone doctor nav.
      /*
       * FOLLOWUP-OWNERSHIP-ROUTING-CONFIG-V1 (tenant ruling R2: the doctor
       * can SEE where an in-window follow-up sits, read-only): the
       * Follow-ups door, gated on the server-computed followupQueueEnabled
       * signal -- present ONLY when the org's followup_ownership config
       * grants this role the queue (window_read_roles names doctor).
       * Default orgs emit no signal, so this door does not exist there
       * (never a dead door -- LES-016 / the #294 pattern; NAV-GATE LAW:
       * nav visibility keys on routing config).
       */
      {
        label: 'Follow-ups',
        to: '/dashboard/coc/patient-followups',
        end: true,
        requiresSignal: 'followupQueueEnabled',
      },
    ],
  },
  /*
   * The front-desk section (03-ROLE-JOURNEYS J7), TRIMMED by founder ruling
   * FO-1 (FRONT-OFFICE-OS-V2-BUILD-V1, 2026-08-01): for the receptionist
   * role the sidebar contains EXACTLY Home | Appointments | Money. This
   * RESOLVES the tracked max-5 role-home breach (the nine-item state the
   * APPOINTMENT-BOOK-V1 / ACCOUNTS-PHASE-1 notes recorded; named resolver
   * RECEPTIONIST-TRIM-V1 -- FO-1 is its founder-ruled landing, at three).
   * HIDING DELETES NOTHING: Workboard, Leads, Lead inbox, Lead to
   * appointment, Patients, and Billing keep their routes, backend
   * contracts, and every other role's access byte-untouched -- only this
   * role's nav advertisement changed. Home = the /walkin role home
   * (FO-2: the landing page is HOME; the day board lives inside it),
   * rendered as a plain Link exactly as before. Money: the daily cash spine
   * (role gate + org/clinic scope are the authority; lawfully reachable,
   * so no signal -- never a dead door, LES-016).
   *
   * DAY-SCOPE-PRIMITIVE-AND-ADOPTION-V1 (founder ruling 2026-08-04, the
   * Section C door adjudication): once Home became day-scoped, the second
   * door had to justify itself on EVIDENCE. It did -- and it is RENAMED, not
   * retired, because the name no longer described the surviving job:
   *   KEEP, because APPOINTMENT-BOOK-V1 owns four jobs Home cannot do --
   *   the PER-DOCTOR lane grid (appointment-calendar-view.ts
   *   buildCalendarColumns), the CONFIGURED time bands with collapsed open
   *   runs (buildCalendarTimeBands / calendarDisplayBands), the roster
   *   columns for doctors with ZERO bookings (the clinic-shape read Home's
   *   row list structurally cannot render), and the availability honesty
   *   seam that rides them. Home's day board is a flat time-ordered
   *   operations table with the doctor as a CELL; the two answer different
   *   questions on the same rows.
   *   RENAME, because "Appointments" named the ROWS -- which Home now
   *   carries for any day -- while the door's surviving job is the day's
   *   SCHEDULE across doctors. The label now says the job.
   * The ROUTE is unchanged (/dashboard/appointments): a nav change is never
   * a permission change, no bookmark or deep link breaks, and the rename is
   * reversible by one word. Creation, rescheduling, slot and availability
   * work were never behind this door (the calendar writes nothing) -- they
   * live on the Lead-360 appointment workbench, so nothing is orphaned.
   */
  receptionist: {
    header: 'Front desk',
    items: [
      { label: 'Home', to: '/walkin', plainLink: true },
      { label: 'Assigned tasks', to: '/dashboard/workboard' },
      { label: 'Money', to: '/dashboard/money' },
      { label: 'Reports', to: '/dashboard/clinic-reports' },
      /* Patient follow-up ownership remains tenant-configured. Partha uses
       * COC from day one; Reception's permitted status/booking lens is in
       * Patient history. Assigned lead tasks above keep their existing scope. */
      {
        label: 'Follow-ups',
        to: '/dashboard/coc/patient-followups',
        end: true,
        requiresSignal: 'followupOwnerQueueEnabled',
      },
      /*
       * FRONT-DESK-DAY-BOARD-V1 (Partha TAB row "No Show Recovery"): the
       * missed-appointment recovery queue door, gated on the server-computed
       * missedAppointmentQueueEnabled signal -- present ONLY when the org's
       * Q-05=D audience config (recovery.missed_appointment_queue_roles)
       * names this role. Under the seeded defaults ('coc' + owner by code)
       * no signal is emitted for this role, so every non-configured org
       * keeps the FO-1 sidebar byte-identically (NAV-GATE LAW; never a dead
       * door, LES-016). Surfacing only: the queue read's own config-audience
       * fence remains the boundary, and no messaging, cron, or escalation
       * rides this door (the retry ladder stays the call centre's).
       */
      {
        label: 'No-shows',
        to: '/dashboard/coc/no-shows',
        end: true,
        requiresSignal: 'missedAppointmentQueueEnabled',
      },
    ],
  },
  /*
   * OPERATOR-JOURNEY-STITCH-V1 (V5-P9): the blended-team call-center
   * section carries the operator surfaces the role actually reaches today
   * (J6's other designed items are gated-out or unbuilt -- shipping them
   * would be dead doors; DELTA-FOR-FOUNDER in the V5-P9 PR).
   * FND-29 (#297): the lawful Leads door (call_center_agent is in
   * LEADS_WORKSPACE_ROLES), signal-gated like every sibling door.
   * THE COMING CALL-CENTER WORKSPACE SLOTS HERE: Deepak's left-nav groups
   * (CALL-CENTER-WORKSPACE-2026-07-19.md s.1) land as further signal-gated
   * items on THIS section when their surfaces + server reads exist -- see
   * the module header COMING-SURFACE SLOT note.
   */
  call_center: {
    header: 'Call center',
    items: [
      /*
       * CALL-CENTER-CRM-WORKSPACE-V1: the agent My Work queue -- the FIRST
       * item filling the COMING-SURFACE SLOT above. Gated on the
       * server-computed callCenterWorkspaceEnabled signal (per-org config
       * callcenter.workspace_enabled, seeded OFF), so the door exists ONLY
       * where the workspace is lawfully reachable (never a dead door;
       * LES-016 / the #294 pattern).
       */
      {
        label: 'My Work',
        to: '/dashboard/callcenter/workspace',
        requiresSignal: 'callCenterWorkspaceEnabled',
      },
      { label: 'Command center', to: '/dashboard/lead-cockpit' },
      {
        label: 'Leads',
        to: '/dashboard/leads',
        end: true,
        requiresSignal: 'leadsWorkspaceEnabled',
      },
      /*
       * CALLCENTRE-EOD-V1: the SLA/lead-chase daily report -- flag-free
       * (unlike My Work above), reusing getSlaLeakage + loadChaseQueue,
       * both already open to call_center_agent with no config gate. Date-
       * navigable (today is simply the default day), never a today-only
       * screen (see the page header for the excluded telephony/
       * agent-performance slice).
       */
      { label: 'Daily report', to: '/dashboard/callcenter/daily-report' },
    ],
  },
};

/** A nav item after gate resolution -- what the renderer actually draws. */
export interface ResolvedNavItem {
  readonly label: string;
  readonly to: string;
  readonly end: boolean;
  readonly plainLink: boolean;
  readonly count?: 'doctorPending';
}

/**
 * The visible sections for a /me payload, in the stable render order.
 * FAIL-CLOSED: null me (loading / errored) -> []; a token in
 * me.navSections that this config does not know -> dropped (an unknown
 * section can never render an unknown link list); patient / any role the
 * backend maps to [] -> [].
 */
export function resolveNavSections(
  me: DashboardMe | null,
): DashboardNavSection[] {
  if (!me) return [];
  return DASHBOARD_NAV_SECTION_ORDER.filter((section) =>
    me.navSections.includes(section),
  );
}

/**
 * The rendered items for one section under a /me payload, gates applied.
 * FAIL-CLOSED: null me or an unknown section -> []; a requiresSignal item
 * renders only while its server-computed signal is strictly true; an
 * onlyRole item renders only for that exact role; signalTo retargets only
 * while its signal is strictly true.
 */
export function resolveNavItems(
  section: DashboardNavSection,
  me: DashboardMe | null,
): ResolvedNavItem[] {
  const config = DASHBOARD_NAV_CONFIG[section] as NavSectionConfig | undefined;
  if (!config || !me) return [];
  if (section === 'call_center' && me.role === 'call_center_manager') {
    const workspace = me.callCenterWorkspaceEnabled === true ? [
      {label:'Team operations',to:'/dashboard/callcenter/workspace',end:true,plainLink:true},
      {label:'Leads',to:'/dashboard/callcenter/workspace?view=leads',end:true,plainLink:true},
      {label:'My team',to:'/dashboard/callcenter/workspace?view=agents',end:true,plainLink:true},
    ] : [];
    return [...workspace,{label:'Reports',to:'/dashboard/callcenter/daily-report',end:true,plainLink:false}];
  }
  const out: ResolvedNavItem[] = [];
  for (const item of config.items) {
    // Ravi, 13 October: Partha's read-only inventory found no Reception or
    // individual assignments. Hide only this menu entry; route grants stay intact.
    if (section === 'receptionist' && me.role === 'receptionist' && me.organizationId === 1 && item.to === '/dashboard/workboard') continue;
    if (me.role === 'call_center_manager' && section === 'call_center' && item.to === '/dashboard/lead-cockpit') continue;
    if (item.onlyRole !== undefined && me.role !== item.onlyRole) continue;
    if (
      item.requiresSignal !== undefined &&
      me[item.requiresSignal] !== true
    ) {
      continue;
    }
    const to =
      item.signalTo !== undefined && me[item.signalTo.signal] === true
        ? item.signalTo.to
        : item.to;
    out.push({
      label: me.role === 'call_center_manager' && item.to === '/dashboard/callcenter/workspace' ? 'Team operations' : me.role === 'call_center_manager' && item.to === '/dashboard/callcenter/daily-report' ? 'Reports' : item.label,
      to,
      end: item.end === true,
      plainLink: item.plainLink === true,
      ...(item.count ? { count: item.count } : {}),
    });
  }
  return out;
}

/** Manager links carry only existing, permitted view context; never an arbitrary query. */
export function managerNavDestination(to: string, location: {pathname:string;search:string}): {to:string;active:boolean} {
  const [pathname, targetSearch='']=to.split('?');
  const target=new URLSearchParams(targetSearch), current=new URLSearchParams(location.search), next=new URLSearchParams();
  for(const key of ['periodStart','periodEnd','source','agentId','queueSource','queueState','queueSearch','agent','due','reason']) {
    const value=current.get(key); if(value!==null)next.set(key,value);
  }
  target.forEach((value,key)=>next.set(key,value));
  const currentView=['leads','agents'].includes(current.get('view')??'')?current.get('view'):null;
  return {to:pathname+(next.size?'?'+next.toString():''),active:location.pathname===pathname&&currentView===target.get('view')};
}
